import { useCallback } from "react";
import { useAppSelector } from "src/store/store";
import { canAccessVlmAudit, hasAuditReadGroup } from "src/utils/permissionUtil";
import { ROLE, ROLE_BY_API, RoleValue } from "./types";

// ----------------------------  Audit Access  ----------------------------
//
// The caller's audit role comes from the DynamoDB permissions registry, via
// GET /audit_access/me (role) and GET /audit_access/my_use_cases (owned use cases):
//
//   Admin         - root role; manages Program Owners and Auditors on every use case.
//                   Admins are not added or changed from the Permissions & Access page.
//   Program Owner - configures the use cases an Admin granted them, and grants
//                   Auditors access to those use cases.
//   Auditor       - audits tasks for their granted use cases; no Configuration access.
//
// The backend enforces all of this; the UI only hides what the caller cannot do.

export interface AuditAccess {
  role: RoleValue | null;
  isAdmin: boolean;
  isProgramOwner: boolean;
  // Admin or Program Owner: may open the Configuration module.
  canManageConfig: boolean;
  // Use cases the caller may configure and staff (every one, for an Admin).
  canManageUseCase: (useCaseId: string) => boolean;
  ownedUseCaseIds: string[];
  // True until the role lookup has settled, so pages can wait instead of bouncing.
  resolving: boolean;
}

export const useAuditAccess = (): AuditAccess => {
  const {
    ldap,
    alias,
    userLoading,
    auditPermissionRole,
    auditPermissionLoading,
    canManageAuditConfig,
    ownedUseCaseIds,
  } = useAppSelector((state) => state.user);

  const role = auditPermissionRole ? ROLE_BY_API[auditPermissionRole] ?? null : null;
  // An LDAP audit-admin already comes back from /me as "Admin".
  const isAdmin = role === ROLE.ADMIN;
  const canManageUseCase = useCallback(
    (useCaseId: string) => isAdmin || ownedUseCaseIds.includes(useCaseId),
    [isAdmin, ownedUseCaseIds],
  );

  // The lookup is skipped (left "idle") for users who cannot call it, so "idle"
  // only means "not settled yet" for users it will run for.
  const willFetch = canAccessVlmAudit(ldap, alias) && hasAuditReadGroup(ldap);
  const resolving =
    userLoading === "idle" ||
    userLoading === "pending" ||
    auditPermissionLoading === "pending" ||
    (auditPermissionLoading === "idle" && willFetch);

  return {
    role,
    isAdmin,
    isProgramOwner: role === ROLE.OWNER,
    // Server-resolved: true only for Admins and Program Owners.
    canManageConfig: canManageAuditConfig,
    canManageUseCase,
    ownedUseCaseIds,
    resolving,
  };
};
